/**
 * Le coupon arrivé par un lien, retenu le temps de l'onglet — [DECISIONS §330].
 *
 * **Ce qui arriverait sans lui.** Un lycéen reçoit
 * `https://haicademy.com/coupon/4KJ9P2WDMX` sur WhatsApp. Il n'a pas de compte :
 * l'application lui montre le formulaire d'inscription, il le remplit, il prouve
 * son adresse — et pendant ce temps l'adresse de la page a changé, ou la page a
 * été rechargée par le navigateur du téléphone à court de mémoire. Le code a
 * disparu. Il est dans le message WhatsApp, mais il faut ressortir de
 * l'application pour aller le rechercher, et le lien ne se laisse pas cliquer
 * deux fois sans repartir de zéro.
 *
 * C'est le même défaut que le §324 — « le code sous les yeux, et nulle part où
 * le taper » — et il se règle de la même façon.
 *
 * **`sessionStorage`, et pas autre chose.** `localStorage` survivrait des
 * semaines et proposerait un code périmé — un coupon expire en sept jours — à
 * quelqu'un qui revient le mois suivant, sur un écran qui n'a rien demandé. Un
 * paramètre d'adresse se perd au premier changement de page. Un état de
 * composant ne survit pas au rechargement, qui est précisément le problème.
 * `sessionStorage` vit le temps de l'onglet : exactement la durée d'une
 * inscription.
 *
 * **Le code n'est pas un secret**, contrairement au code de vérification du
 * §324 : c'est un jeton à usage unique, écrit en clair sur une image qu'on
 * imprime et qu'on distribue. L'écrire sur le disque du navigateur n'expose
 * rien que le message WhatsApp n'expose déjà.
 */

const CLE = 'haicademy:coupon-en-attente'

export function retenirLeCoupon(code: string): void {
  try {
    sessionStorage.setItem(CLE, code)
  } catch {
    // Navigation privée, quota plein, stockage refusé : le candidat garde son
    // lien et son code à l'écran. On ne fait pas échouer une arrivée qui, elle,
    // fonctionne.
  }
}

/**
 * Le code retenu, ou `null`.
 *
 * **On se méfie de ce qu'on relit.** Le contenu vient du disque : une version
 * antérieure de l'application a pu y écrire une autre forme, et un objet là où
 * l'écran attend une chaîne ferait planter le montage — c'est-à-dire au pire
 * moment, celui du retour.
 */
export function couponEnAttente(): string | null {
  try {
    const lu = sessionStorage.getItem(CLE)

    return typeof lu === 'string' && lu !== '' ? lu : null
  } catch {
    return null
  }
}

/** Le code a servi, ou il a été refusé : on ne le repropose pas à chaque écran. */
export function oublierLeCoupon(): void {
  try {
    sessionStorage.removeItem(CLE)
  } catch {
    // Voir ci-dessus : l'absence de stockage ne doit rien casser.
  }
}
